/**
 * Système de toast centralisé pour l'application
 * Affiche des notifications temporaires pour les messages de succès, erreur, info et avertissement
 */

// Types de toast disponibles
type ToastType = 'success' | 'error' | 'info' | 'warning';

// Configuration des couleurs et styles par type de toast
const toastStyles: Record<ToastType, { bgColor: string, textColor: string }> = {
  success: { bgColor: '#4caf50', textColor: 'white' },
  error: { bgColor: '#f44336', textColor: 'white' },
  info: { bgColor: '#2196f3', textColor: 'white' },
  warning: { bgColor: '#ff9800', textColor: 'white' }
};

// Durée d'affichage par défaut en ms pour chaque type de toast
const defaultDurations: Record<ToastType, number> = {
  success: 3000,
  error: 5000,
  info: 3000,
  warning: 4000
};

// Gestion des toasts actifs
interface ToastItem {
  id: number;
  element: HTMLElement;
  type: ToastType;
  timerId: number;
}

// Stockage des toasts actifs
const activeToasts: ToastItem[] = [];

// Compteur pour générer des IDs uniques
let toastIdCounter = 0;

// Espacement vertical entre les toasts
const TOAST_SPACING = 10; // pixels

/**
 * Repositionne tous les toasts actifs pour éviter les chevauchements
 */
const repositionToasts = (): void => {
  let currentTop = 20; // Position initiale depuis le haut
  
  activeToasts.forEach(toast => {
    toast.element.style.top = `${currentTop}px`;
    currentTop += toast.element.offsetHeight + TOAST_SPACING;
  });
};

/**
 * Crée et affiche un toast avec le style correspondant au type
 * @param type Type de toast (success, error, info, warning)
 * @param message Message à afficher
 * @param duration Durée d'affichage en ms (optionnel)
 */
const showToast = (type: ToastType, message: string, duration?: number): void => {
  // Création de l'élément toast
  const toastDiv = document.createElement('div');
  toastDiv.textContent = message;
  
  // Application des styles
  const { bgColor, textColor } = toastStyles[type];
  toastDiv.style.cssText = `
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    background-color: ${bgColor};
    color: ${textColor};
    padding: 12px 16px;
    border-radius: 4px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    z-index: 11000;
    font-family: system-ui, -apple-system, sans-serif;
    font-size: 14px;
    max-width: 80%;
    text-align: center;
    opacity: 0;
    transition: opacity 0.3s ease-in-out, top 0.3s ease-in-out;
  `;
  
  // Ajout au DOM
  document.body.appendChild(toastDiv);
  
  // Générer un ID unique pour ce toast
  const toastId = toastIdCounter++;
  
  // Ajouter à la liste des toasts actifs
  const toastItem: ToastItem = {
    id: toastId,
    element: toastDiv,
    type,
    timerId: 0
  };
  
  activeToasts.push(toastItem);
  
  // Repositionner tous les toasts
  repositionToasts();
  
  // Animation d'apparition
  setTimeout(() => {
    toastDiv.style.opacity = '1';
  }, 10);
  
  // Disparition automatique après la durée spécifiée
  const timerId = window.setTimeout(() => {
    // Retirer de la liste des toasts actifs
    const index = activeToasts.findIndex(toast => toast.id === toastId);
    if (index !== -1) {
      activeToasts.splice(index, 1);
    }
    
    // Animation de disparition
    toastDiv.style.opacity = '0';
    
    // Supprimer du DOM après l'animation
    setTimeout(() => {
      if (document.body.contains(toastDiv)) {
        document.body.removeChild(toastDiv);
      }
      // Repositionner les toasts restants
      repositionToasts();
    }, 300); // Attendre la fin de l'animation de disparition
  }, duration || defaultDurations[type]);
  
  // Enregistrer le timer ID
  toastItem.timerId = timerId;
  
  // Permettre de fermer le toast en cliquant dessus
  toastDiv.addEventListener('click', () => {
    // Annuler le timer de disparition automatique
    clearTimeout(timerId);
    
    // Retirer de la liste des toasts actifs
    const index = activeToasts.findIndex(toast => toast.id === toastId);
    if (index !== -1) {
      activeToasts.splice(index, 1);
    }
    
    // Animation de disparition
    toastDiv.style.opacity = '0';
    
    // Supprimer du DOM après l'animation
    setTimeout(() => {
      if (document.body.contains(toastDiv)) {
        document.body.removeChild(toastDiv);
      }
      // Repositionner les toasts restants
      repositionToasts();
    }, 300);
  });
};

// Export de l'API publique
export const toast = {
  success: (message: string, duration?: number) => showToast('success', message, duration),
  error: (message: string, duration?: number) => showToast('error', message, duration),
  info: (message: string, duration?: number) => showToast('info', message, duration),
  warning: (message: string, duration?: number) => showToast('warning', message, duration),
  
  /**
   * Ferme tous les toasts actifs
   */
  dismissAll: () => {
    // Copier le tableau pour éviter les problèmes lors de la suppression d'éléments
    const toastsCopy = [...activeToasts];
    
    // Fermer chaque toast
    toastsCopy.forEach(toast => {
      // Annuler le timer
      clearTimeout(toast.timerId);
      
      // Animation de disparition
      toast.element.style.opacity = '0';
      
      // Supprimer du DOM après l'animation
      setTimeout(() => {
        if (document.body.contains(toast.element)) {
          document.body.removeChild(toast.element);
        }
      }, 300);
    });
    
    // Vider le tableau des toasts actifs
    activeToasts.length = 0;
  }
};

